/**
 * Erros da API → mensagens amigáveis.
 * Formato do servidor: { error: { code, message, details } } (apps/server/src/lib/errors.ts).
 */
import { hasKey, t, type TKey } from "@/i18n";

export type ApiErrorBody = { error?: { code?: string; message?: string; details?: unknown } | string };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** Mensagem original do servidor (útil para depuração). */
  readonly serverMessage: string | undefined;

  constructor(status: number, code: string, serverMessage?: string, details?: unknown) {
    super(friendlyMessage(code, status, serverMessage));
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.serverMessage = serverMessage;
  }

  /** Cria a partir do corpo de erro (tolera corpos fora do padrão, ex.: `{ error: "x" }`). */
  static fromBody(status: number, body: unknown): ApiError {
    const b = (body ?? {}) as ApiErrorBody;
    if (typeof b.error === "string") return new ApiError(status, b.error);
    const code = b.error?.code ?? codeForStatus(status);
    return new ApiError(status, code, b.error?.message, b.error?.details);
  }

  /** Detalhes de validação por campo ({ path, message }[]), quando houver. */
  get fieldErrors(): { path: string; message: string }[] {
    return Array.isArray(this.details)
      ? (this.details as { path?: unknown; message?: unknown }[])
          .filter((d) => typeof d?.message === "string")
          .map((d) => ({ path: String(d.path ?? ""), message: String(d.message) }))
      : [];
  }
}

/** Código padrão quando o corpo não traz um. */
export function codeForStatus(status: number): string {
  if (status === 0) return "network";
  if (status === 401) return "unauthorized";
  if (status === 402) return "subscription_required";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "internal_error";
  return "unknown";
}

/**
 * Prioridade: tradução do código → mensagem do servidor (já vem em pt-BR) →
 * tradução genérica pelo status.
 */
export function friendlyMessage(code: string, status: number, serverMessage?: string): string {
  const key = `errors.${code}`;
  if (hasKey(key)) return t(key);
  if (serverMessage && status < 500) return serverMessage;
  const fallback = `errors.${codeForStatus(status)}` as TKey;
  return t(hasKey(fallback) ? fallback : "errors.unknown");
}

/** Mensagem amigável para qualquer erro (ApiError, erro do better-auth, Error genérico). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err && typeof err === "object") {
    const e = err as { code?: unknown; message?: unknown; status?: unknown };
    if (typeof e.code === "string") {
      const key = `authErrors.${e.code}`;
      if (hasKey(key)) return t(key);
    }
    if (e.status === 429) return t("errors.rate_limited");
    if (typeof e.message === "string" && e.message) return e.message;
  }
  return t("errors.unknown");
}

export const isApiError = (err: unknown, code?: string): err is ApiError =>
  err instanceof ApiError && (code === undefined || err.code === code);
